import React from 'react';
import {
  useQuery,
  getLocationBySlug,
  getEvents,
  getTenants,
  getLocationNotices,
  getFoodCourtItems,
  getFacilities,
  getServices,
  getTeamMembers,
} from 'wasp/client/operations';
import { REGULAR_EVENTS_LIST_FILTERS } from '../events/eventFilters';
import { useAuth } from 'wasp/client/auth';
import {
  Building2,
  Calendar,
  Users,
  Utensils,
  Megaphone,
  Zap,
} from 'lucide-react';
import { OpeningHoursPanel } from '../information-desk/OpeningHoursPanel';
import { HubTeamSection } from './HubTeamSection';
import {
  formatEventTime,
  isHappeningNow,
  SectionCard,
  ItemRow,
} from './hubOverviewShared';
import { HubTodayEvents } from './HubTodayEvents';
import { SubscriptionButton } from '../../components/subscriptions/SubscriptionButton';

interface Props {
  slug: string;
  locationId?: string;
  children?: React.ReactNode;
}

export function LocationHubOverview({ slug, locationId: locationIdProp, children }: Props) {
  const { data: user } = useAuth();
  const isAdmin = !!user && (
    user.platformRole === 'ADMINISTRATOR' ||
    user.platformRole === 'PLATFORM_OWNER' ||
    user.platformRole === 'MODERATOR'
  );

  const { data: loc, isLoading: locLoading, refetch: refetchLocation } = useQuery(
    getLocationBySlug,
    { slug },
    { enabled: !!slug }
  ) as { data: any; isLoading: boolean; refetch: () => void };

  const locationId = locationIdProp ?? loc?.id;
  const enabled = { enabled: !!locationId };

  const { data: events = [] } = useQuery(
    getEvents,
    { locationId, filters: REGULAR_EVENTS_LIST_FILTERS },
    enabled
  ) as { data: any[] };
  const { data: tenants = [] } = useQuery(getTenants, { filters: { locationId } }, enabled) as { data: any[] };
  const { data: notices = [] } = useQuery(getLocationNotices, { locationIds: locationId ? [locationId] : [] }, enabled) as { data: any[] };
  const { data: foodItems = [] } = useQuery(getFoodCourtItems, { locationIds: locationId ? [locationId] : [] }, enabled) as { data: any[] };
  const { data: facilities = [] } = useQuery(getFacilities, { locationId }, enabled) as { data: any[] };
  const { data: services = [] } = useQuery(getServices, { locationId }, enabled) as { data: any[] };
  const { data: teamMembers = [] } = useQuery(getTeamMembers, { locationId }, enabled) as { data: any[] };

  const base = `/${slug}`;

  const upcomingEvents = events
    .filter((e: any) => new Date(e.endTime) >= new Date())
    .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    .slice(0, 5);

  if (locLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!loc) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Building2 className="w-10 h-10 mr-3" />
        <span>Location not found.</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="hub-hero bg-gradient-to-br from-indigo-50 via-sky-50 to-white rounded-2xl p-4 sm:p-6 shadow-sm border border-indigo-100/80">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight text-gray-900 mb-2">{loc.name}</h1>
            <p className="text-gray-600 text-sm leading-relaxed max-w-xl">
              Welcome to the official hub for {loc.name}. Access services, community notices, upcoming events, and find key tenant information within this location.
            </p>
            {user && loc.id && (
              <div className="mt-4">
                <SubscriptionButton
                  type="LOCATION"
                  id={loc.id}
                  locationSlug={slug}
                  isSubscribed={loc.membership?.isSubscribed ?? false}
                  onSuccess={() => refetchLocation()}
                />
              </div>
            )}
          </div>

          {locationId && <OpeningHoursPanel locationId={locationId} isAdmin={isAdmin} variant="hero" />}
        </div>
      </div>

      {locationId && <HubTodayEvents locationId={locationId} orgSlug={slug} />}

      {children}

      <HubTeamSection members={teamMembers} accent="indigo" />

      {/* Directory preview cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SectionCard
          title="Events & Activities"
          icon={<Calendar className="w-4 h-4" />}
          color="bg-indigo-50 text-indigo-700 border-indigo-100/55"
          count={events.length}
          seeAllHref={`${base}/events`}
          isEmpty={upcomingEvents.length === 0}
          emptyLabel="No upcoming events."
        >
          <div className="space-y-0.5">
            {upcomingEvents.map((event: any) => (
              <div key={event.id} className="py-1.5 border-b border-gray-50 last:border-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-850 truncate flex-1">{event.title}</p>
                  {isHappeningNow(event.startTime, event.endTime) && (
                    <span className="flex-shrink-0 flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Live
                    </span>
                  )}
                </div>
                <p className="text-xs text-indigo-500 mt-0.5 font-medium">{formatEventTime(event.startTime, event.endTime)}</p>
                {event.description && (
                  <p className="text-xs text-gray-400 truncate mt-0.5">{event.description}</p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title="Tenant Directory"
          icon={<Users className="w-4 h-4" />}
          color="bg-violet-50 text-violet-700 border-violet-100/55"
          count={tenants.length}
          seeAllHref={`${base}/tenants`}
          isEmpty={tenants.length === 0}
          emptyLabel="No tenants listed yet."
        >
          <div className="grid grid-cols-1 gap-1">
            {tenants.slice(0, 6).map((tenant: any) => (
              <div key={tenant.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <span className="text-xs font-bold text-violet-600">
                    {(tenant.companyName ?? tenant.title ?? '?').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-805 truncate">{tenant.companyName ?? tenant.title}</p>
                  {tenant.category && (
                    <p className="text-xs text-gray-400 truncate">{tenant.category}</p>
                  )}
                </div>
              </div>
            ))}
            {tenants.length > 6 && (
              <p className="text-xs text-gray-400 pt-1.5 font-medium">+{tenants.length - 6} more tenants</p>
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Food Court"
          icon={<Utensils className="w-4 h-4" />}
          color="bg-orange-50 text-orange-700 border-orange-100/55"
          count={foodItems.length}
          seeAllHref={`${base}/food-court`}
          isEmpty={foodItems.length === 0}
          emptyLabel="No food listings yet."
        >
          <div>
            {foodItems.slice(0, 5).map((item: any) => (
              <ItemRow key={item.id} label={item.title} sub={item.description} />
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard
          title="Facilities"
          icon={<Building2 className="w-4 h-4" />}
          color="bg-cyan-50 text-cyan-700 border-cyan-100/55"
          count={facilities.length}
          seeAllHref={`${base}/facilities`}
          isEmpty={facilities.length === 0}
          emptyLabel="No facilities listed."
        >
          {facilities.slice(0, 5).map((facility: any) => (
            <ItemRow key={facility.id} label={facility.title} sub={facility.description} badge={facility.category} />
          ))}
        </SectionCard>

        <SectionCard
          title="Services"
          icon={<Zap className="w-4 h-4" />}
          color="bg-emerald-50 text-emerald-700 border-emerald-100/55"
          count={services.length}
          seeAllHref={`${base}/services`}
          isEmpty={services.length === 0}
          emptyLabel="No services listed."
        >
          {services.slice(0, 5).map((service: any) => (
            <ItemRow key={service.id} label={service.title} sub={service.description} badge={service.category} />
          ))}
        </SectionCard>
      </div>
    </div>
  );
}
